import { ArrowRight, ChevronDown } from 'lucide-react'

import type { AuthorityContinuityViewModel } from '../lib/authority-continuity-view-model'

export interface AuthorityContinuityRegionProps {
  model: AuthorityContinuityViewModel
  onContinueChat: () => void
  onReviewContract: () => void
  onOpenConformance: () => void
}

const TONE_CLASSES: Record<AuthorityContinuityViewModel['tone'], string> = {
  neutral: 'border-border bg-card',
  'needs-input': 'border-primary/30 bg-primary/5',
  ready: 'border-border bg-card',
  blocked: 'border-destructive/30 bg-destructive/5',
}

export function AuthorityContinuityRegion({
  model,
  onContinueChat,
  onReviewContract,
  onOpenConformance,
}: AuthorityContinuityRegionProps) {
  const headingId = 'authority-continuity-title'
  const commandHandlers = {
    'continue-chat': onContinueChat,
    'review-contract': onReviewContract,
    'open-conformance': onOpenConformance,
  } satisfies Record<
    NonNullable<AuthorityContinuityViewModel['primaryAction']['command']>,
    () => void
  >
  const primaryCommand = model.primaryAction.command
  const commandAllowed =
    primaryCommand !== undefined &&
    !(model.tone === 'blocked' && primaryCommand === 'continue-chat')
  const hrefAllowed = model.tone !== 'blocked' && Boolean(model.primaryAction.href)
  const showSecondary = model.tone !== 'blocked' && model.secondaryAction
  const actionClassName =
    'inline-flex min-h-11 w-full max-w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-center text-sm font-semibold leading-5 whitespace-normal text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:w-auto'

  return (
    <section
      data-testid="authority-continuity-region"
      data-tone={model.tone}
      aria-labelledby={headingId}
      className={`w-full max-w-full min-w-0 overflow-hidden rounded-xl border shadow-[0_1px_2px_rgb(15_23_42_/_0.03)] ${TONE_CLASSES[model.tone]}`}
    >
      <details className="group">
        <summary className="min-h-12 cursor-pointer list-none px-4 py-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring [&::-webkit-details-marker]:hidden sm:px-5">
          <h2
            id={headingId}
            aria-label={model.title}
            className="flex min-w-0 items-center justify-between gap-3"
          >
            <span className="min-w-0">
              <span
                aria-hidden
                className="block text-[11px] font-bold uppercase tracking-[0.08em] text-muted-foreground"
              >
                {model.eyebrow}
              </span>
              <span className="mt-0.5 block truncate text-sm font-semibold text-foreground sm:text-base">
                {model.title}
              </span>
            </span>
            <ChevronDown
              className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180"
              aria-hidden
            />
          </h2>
        </summary>

        <div className="border-t border-border px-4 pb-5 pt-4 sm:px-5">
          <p className="max-w-[640px] text-sm leading-6 text-muted-foreground">
            {model.summary}
          </p>

          {model.facts.length > 0 ? (
            <dl className="mt-4 grid min-w-0 gap-3 border-t border-border pt-4 sm:grid-cols-2">
              {model.facts.map((fact, index) => (
                <div
                  key={`${fact.label}:${index}`}
                  data-testid={`authority-continuity-fact-${index}`}
                  className="min-w-0"
                >
                  <dt className="break-words text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                    {fact.label}
                  </dt>
                  <dd className="mt-1 min-w-0 break-words text-sm leading-5 text-foreground [overflow-wrap:anywhere]">
                    {fact.value}
                  </dd>
                </div>
              ))}
            </dl>
          ) : null}

          {showSecondary ? (
            <div className="mt-5 flex min-w-0 flex-col items-stretch gap-2 sm:flex-row sm:items-center">
              <a
                href={showSecondary.href}
                className="inline-flex min-h-11 max-w-full items-center justify-center rounded-lg px-3 py-2 text-center text-sm font-semibold leading-5 whitespace-normal text-primary hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {showSecondary.label}
              </a>
            </div>
          ) : null}
        </div>
      </details>

      {commandAllowed && primaryCommand ? (
        <div className="border-t border-border px-4 pb-4 pt-3 sm:px-5">
          <button
            type="button"
            onClick={commandHandlers[primaryCommand]}
            className={actionClassName}
          >
            <span className="min-w-0 break-words">
              {model.primaryAction.label}
            </span>
            <ArrowRight className="h-4 w-4 shrink-0" aria-hidden />
          </button>
        </div>
      ) : hrefAllowed && model.primaryAction.href ? (
        <div className="border-t border-border px-4 pb-4 pt-3 sm:px-5">
          <a href={model.primaryAction.href} className={actionClassName}>
            <span className="min-w-0 break-words">
              {model.primaryAction.label}
            </span>
            <ArrowRight className="h-4 w-4 shrink-0" aria-hidden />
          </a>
        </div>
      ) : null}
    </section>
  )
}
